import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, User } from "lucide-react";
import { api, formatDate, imgUrl } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { EpisodeHero } from "@/components/EpisodeHero";
import { EpisodeCard } from "@/components/EpisodeCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

const PAGE = 12;

export default function HostPage() {
  const { slug } = useParams();
  const [host, setHost] = useState(null);
  const [error, setError] = useState(false);
  const [visible, setVisible] = useState(PAGE);

  useEffect(() => {
    setHost(null); setError(false); setVisible(PAGE);
    api.get(`/hosts/${slug}`, { params: { limit: 5000 } }).then((r) => setHost(r.data)).catch(() => setError(true));
  }, [slug]);

  const eps = host?.episodes || [];

  return (
    <div className="min-h-screen flex flex-col" data-testid="host-page">
      <Navbar />
      <main className="flex-1 w-full max-w-[1400px] mx-auto px-5 sm:px-8 pt-8 pb-32">
        <Link to="/#team" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-8" data-testid="host-back-link"><ArrowLeft size={16} /> Back to home</Link>
        {error ? (
          <div className="card-surface rounded-3xl p-12 text-center text-slate-400" data-testid="host-not-found">Host not found.</div>
        ) : !host ? (
          <Skeleton className="h-72 rounded-3xl bg-white/5" />
        ) : (
          <>
            <section className="card-surface grain relative overflow-hidden rounded-3xl p-6 sm:p-10" data-testid="host-header">
              <div className="absolute -top-32 -right-32 h-80 w-80 rounded-full bg-[#053671] blur-3xl opacity-60 pointer-events-none" />
              <div className="relative grid md:grid-cols-[200px_1fr] gap-8 items-center">
                <div className="relative mx-auto md:mx-0">
                  <div className="absolute inset-0 rounded-full bg-sky-400/30 blur-2xl" />
                  {host.image_url ? <img src={imgUrl(host.image_url)} alt={host.name} className="relative h-44 w-44 sm:h-48 sm:w-48 rounded-full object-cover ring-4 ring-sky-400/40" data-testid="host-image" /> : <div className="relative h-44 w-44 sm:h-48 sm:w-48 rounded-full bg-[#053671] ring-4 ring-sky-400/40 flex items-center justify-center"><User size={56} className="text-sky-200" /></div>}
                </div>
                <div className="text-center md:text-left">
                  <p className="eyebrow mb-2">{host.role}</p>
                  <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none" data-testid="host-name">{host.name}</h1>
                  {host.bio && <p className="mt-5 text-slate-300 leading-relaxed max-w-2xl" data-testid="host-bio">{host.bio}</p>}
                  <div className="mt-5 flex flex-wrap justify-center md:justify-start gap-3 text-sm">
                    <span className="rounded-full bg-white/5 px-3 py-1 font-mono text-xs text-slate-300" data-testid="host-show-count">{host.shows.length} show{host.shows.length === 1 ? "" : "s"}</span>
                    <span className="rounded-full bg-white/5 px-3 py-1 font-mono text-xs text-slate-300" data-testid="host-episode-count">{host.episode_count} episodes</span>
                  </div>
                </div>
              </div>
            </section>

            <section className="mt-14" data-testid="host-shows-section">
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mb-6">Shows</h2>
              {host.shows.length === 0 ? (
                <p className="text-slate-400" data-testid="host-no-shows">No shows assigned yet.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {host.shows.map((s, i) => (
                    <Link key={s.id} to={`/shows/${s.slug}`} className="group card-surface card-surface-hover rounded-2xl p-3 reveal" style={{ animationDelay: `${i * 40}ms` }} data-testid={`host-show-${s.slug}`}>
                      <div className="aspect-square rounded-xl overflow-hidden">
                        {s.cover_url ? <img src={s.cover_url} alt={s.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" /> : <div className="h-full w-full bg-[#053671]" />}
                      </div>
                      <div className="mt-3 flex items-start gap-2">
                        <h3 className="font-display font-bold leading-snug text-sm">{s.name}</h3>
                        {(s.kind === "video" || s.kind === "tiktok") && <span className="ml-auto mt-0.5 shrink-0 rounded-full bg-rose-500/15 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider text-rose-400" data-testid={`host-show-video-badge-${s.slug}`}>Video</span>}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>

            <section className="mt-16" data-testid="host-episodes-section">
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mb-6">Episodes</h2>
              {eps.length === 0 ? (
                <p className="text-slate-400" data-testid="host-no-episodes">No episodes yet — check back soon.</p>
              ) : (
                <>
                  <div data-testid="host-hero"><EpisodeHero episode={eps[0]} eyebrow={`Latest · ${formatDate(eps[0].published_at)}`} testId="host-hero" /></div>
                  {eps.length > 1 && (
                    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="host-episodes-grid">
                      {eps.slice(1, visible).map((ep, i) => <EpisodeCard key={ep.id} episode={ep} index={i + 1} />)}
                    </div>
                  )}
                  {visible < eps.length && (
                    <div className="mt-8 flex justify-center">
                      <Button onClick={() => setVisible((v) => v + PAGE)} variant="outline" className="rounded-full border-white/15 bg-transparent hover:bg-white/10 px-8" data-testid="host-load-more">Load more episodes</Button>
                    </div>
                  )}
                </>
              )}
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
